import { Box } from "@skailer-com/uikit";
import { useId } from "react";

interface Props {
  radius?: number;
  strokeWidth?: number;
  startBlur?: number;
  endBlur?: number;
  startColor?: string;
  endColor?: string;
  /** Blur direction angle in degrees. 0 = top to bottom, 90 = left to right, 180 = bottom to top, 270 = right to left */
  angleBlur?: number;
  /** Color gradient angle in degrees. 0 = top to bottom, 90 = left to right, 180 = bottom to top, 270 = right to left */
  angleGradient?: number;
  strokeOpacity?: number;
  colorSpace?: "linearRGB" | "sRGB";
}

export default function ProgressiveBlurCircle({
  radius = 100,
  strokeWidth = 21,
  startBlur = 10,
  endBlur = 54,
  startColor = "#FD5151",
  endColor = "#F5AC3E",
  angleBlur = 0,
  angleGradient = 0,
  strokeOpacity = 1,
  colorSpace = "linearRGB",
}: Props) {
  const isUniformBlur = startBlur === endBlur;
  const sdLo = startBlur / 2;
  const sdMid = (startBlur + endBlur) / 4;
  const sdHi = endBlur / 2;

  // Padding = radius + half-stroke + 5σ of max blur + small margin
  const pad = radius + strokeWidth / 2 + sdHi * 5 + 20;
  const size = pad * 2;
  const cx = pad;
  const cy = pad;
  const gradRad = (angleGradient * Math.PI) / 180;
  const gradSin = Math.sin(gradRad);
  const gradCos = Math.cos(gradRad);
  const gradientRadius = radius + strokeWidth / 2;

  // Stroke gradient endpoints (userSpaceOnUse)
  const strokeGradient = {
    x1: cx - gradSin * gradientRadius,
    y1: cy - gradCos * gradientRadius,
    x2: cx + gradSin * gradientRadius,
    y2: cy + gradCos * gradientRadius,
  };

  const blurRad = (angleBlur * Math.PI) / 180;
  const blurSin = Math.sin(blurRad);
  const blurCos = Math.cos(blurRad);

  // Mask gradient direction (objectBoundingBox: 0 to 1)
  const maskGradient = {
    x1: 0.5 - blurSin * 0.5,
    y1: 0.5 - blurCos * 0.5,
    x2: 0.5 + blurSin * 0.5,
    y2: 0.5 + blurCos * 0.5,
  };

  const filterRegion = { x: "-200%", y: "-200%", width: "500%", height: "500%" };

  const id = useId();
  const ids = {
    stroke: `blur-stroke-${id}`,
    f1: `blur-f1-${id}`,
    f2: `blur-f2-${id}`,
    f3: `blur-f3-${id}`,
    gTop: `blur-gTop-${id}`,
    gMid: `blur-gMid-${id}`,
    gBot: `blur-gBot-${id}`,
    mTop: `blur-mTop-${id}`,
    mMid: `blur-mMid-${id}`,
    mBot: `blur-mBot-${id}`,
  };

  return (
    <Box
      className={`relative`}
      style={{
        width: `${radius * 2}px`,
        aspectRatio: 1,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: "visible" }}
        className={"absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"}
      >
        <defs>
          <linearGradient
            id={ids.stroke}
            x1={strokeGradient.x1}
            y1={strokeGradient.y1}
            x2={strokeGradient.x2}
            y2={strokeGradient.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={startColor} />
            <stop offset="100%" stopColor={endColor} />
          </linearGradient>

          <filter id={ids.f1} {...filterRegion} colorInterpolationFilters={colorSpace}>
            <feGaussianBlur stdDeviation={sdLo} />
          </filter>
          <filter id={ids.f2} {...filterRegion} colorInterpolationFilters={colorSpace}>
            <feGaussianBlur stdDeviation={sdMid} />
          </filter>
          <filter id={ids.f3} {...filterRegion} colorInterpolationFilters={colorSpace}>
            <feGaussianBlur stdDeviation={sdHi} />
          </filter>

          <linearGradient
            id={ids.gTop}
            x1={maskGradient.x1}
            y1={maskGradient.y1}
            x2={maskGradient.x2}
            y2={maskGradient.y2}
          >
            <stop offset="15%" stopColor="white" stopOpacity={1} />
            <stop offset="50%" stopColor="white" stopOpacity={0} />
          </linearGradient>
          <linearGradient
            id={ids.gMid}
            x1={maskGradient.x1}
            y1={maskGradient.y1}
            x2={maskGradient.x2}
            y2={maskGradient.y2}
          >
            <stop offset="15%" stopColor="white" stopOpacity={0} />
            <stop offset="38%" stopColor="white" stopOpacity={1} />
            <stop offset="62%" stopColor="white" stopOpacity={1} />
            <stop offset="85%" stopColor="white" stopOpacity={0} />
          </linearGradient>
          <linearGradient
            id={ids.gBot}
            x1={maskGradient.x1}
            y1={maskGradient.y1}
            x2={maskGradient.x2}
            y2={maskGradient.y2}
          >
            <stop offset="50%" stopColor="white" stopOpacity={0} />
            <stop offset="85%" stopColor="white" stopOpacity={1} />
          </linearGradient>

          <mask id={ids.mTop} maskUnits="userSpaceOnUse" x="0" y="0" width={size} height={size}>
            <rect width={size} height={size} fill={`url(#${ids.gTop})`} />
          </mask>
          <mask id={ids.mMid} maskUnits="userSpaceOnUse" x="0" y="0" width={size} height={size}>
            <rect width={size} height={size} fill={`url(#${ids.gMid})`} />
          </mask>
          <mask id={ids.mBot} maskUnits="userSpaceOnUse" x="0" y="0" width={size} height={size}>
            <rect width={size} height={size} fill={`url(#${ids.gBot})`} />
          </mask>
        </defs>

        {isUniformBlur ? (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={`url(#${ids.stroke})`}
            strokeWidth={strokeWidth}
            strokeOpacity={strokeOpacity}
            filter={`url(#${ids.f1})`}
          />
        ) : (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={`url(#${ids.stroke})`}
              strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              filter={`url(#${ids.f1})`}
              mask={`url(#${ids.mTop})`}
            />
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={`url(#${ids.stroke})`}
              strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              filter={`url(#${ids.f2})`}
              mask={`url(#${ids.mMid})`}
            />
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={`url(#${ids.stroke})`}
              strokeWidth={strokeWidth}
              strokeOpacity={strokeOpacity}
              filter={`url(#${ids.f3})`}
              mask={`url(#${ids.mBot})`}
            />
          </>
        )}
      </svg>
    </Box>
  );
}
